import { useEffect, useCallback, useState } from "react";

// Content canvas max-width (from ContentContainer) + 32px breathing room
const BAR_MAX_WIDTH = 932;

export interface BarPosition {
  left: number;
  right: number;
  maxWidth?: number;
}

/**
 * Calculates the left and right position for the simple inspector bar
 * by observing the size of `#inspector-view-wrapper` with a ResizeObserver.
 */
export function useInspectorBarPosition(
  inspectorPanelPeak: number,
  constrainWidth = true
): BarPosition {
  const [barPosition, setBarPosition] = useState<BarPosition>({
    left: 0,
    right: 0,
    maxWidth: constrainWidth ? BAR_MAX_WIDTH : undefined,
  });

  const calculatePosition = useCallback(
    (wrapperElement: HTMLElement) => {
      const wrapperPosition = wrapperElement.getBoundingClientRect();
      const scrollbarWidth = wrapperElement.offsetWidth - wrapperElement.clientWidth || 0;
      const rightOffsetCalculation = scrollbarWidth + inspectorPanelPeak;

      const leftOffset = wrapperPosition.left + 16,
        rightOffset = window.innerWidth - wrapperPosition.right + rightOffsetCalculation + 16;
      setBarPosition({
        left: leftOffset,
        right: rightOffset,
        maxWidth: constrainWidth ? BAR_MAX_WIDTH : undefined,
      });
    },
    [inspectorPanelPeak, constrainWidth]
  );

  useEffect(() => {
    const wrapperElement = document.querySelector("#inspector-view-wrapper") as HTMLElement;
    if (!wrapperElement) return;

    calculatePosition(wrapperElement);

    const observer = new ResizeObserver(() => calculatePosition(wrapperElement));
    observer.observe(wrapperElement);

    return () => {
      observer.disconnect();
    };
  }, [calculatePosition]);

  return barPosition;
}
